import { WebGebruikerForm } from '@/lib/components/forms/WebGebruikerForm';
import { constants } from '@/lib/constants';
import { Roles, useUserContext } from '@/lib/context/userContext';
import { useGetBevoegdGezagenSummary, useGetMilieudiensten } from '@/lib/queries';
import { useGetDeelnemers } from '@/lib/queries/config-api/deelnemer';
import { useGetDeskundigen } from '@/lib/queries/config-api/deskundige';
import { useGetWebgebruikerById, useUpdateWebgebruiker } from '@/lib/queries/config-api/webGebruiker';
import { ToelichtingsTypeEnum, WebGebruikerToelichting } from '@/lib/types';
import { Button, Card, Divider, List, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

const { Text } = Typography;

type ChangeLogProps = {
  items: WebGebruikerToelichting[];
  loading: boolean;
  title: string;
  emptyText: string;
};

const ChangeLog: React.FunctionComponent<ChangeLogProps> = ({ items, loading, title, emptyText }) => {
  return (
    <div style={{ maxWidth: '50%', margin: '0 auto' }}>
      <Divider>{title}</Divider>
      <br />
      <List
        itemLayout="horizontal"
        loading={loading}
        dataSource={items}
        locale={{ emptyText }}
        renderItem={item => (
          <List.Item>
            <List.Item.Meta
              title={
                <Text style={{ textTransform: 'initial' }}>
                  ({ToelichtingsTypeEnum[item.type]}) {item.toelichting}
                </Text>
              }
              description={`${item.aangemaaktDoorGebruikerEmail} - ${new Date(item.aangemaaktOp).toLocaleDateString()}`}
            />
          </List.Item>
        )}
      />
    </div>
  );
};

export const WebGebruikerEdit: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const id = Number(useParams().id?.toString());
  const updateWebgebruiker = useUpdateWebgebruiker(id);
  const { t } = useTranslation('core');
  const { roles } = useUserContext();
  const isReadOnly = !roles.includes(Roles.Administrator);

  const { data: webGebruikerResponse, isLoading: isWebGebruikerLoading } = useGetWebgebruikerById(id);
  const { data: milieudienstenResponse, isLoading: isMilieudienstenLoading } = useGetMilieudiensten({ pageSize: constants.defaultMaxPageSize });
  const { data: bevoegdGezagResponse, isLoading: isBevoegdGezagLoading } = useGetBevoegdGezagenSummary();
  const { data: deelnemerResponse, isLoading: isDeelnemerLoading } = useGetDeelnemers({ pageSize: constants.defaultMaxPageSize });
  const { data: deskundigenResponse, isLoading: isDeskundigenLoading } = useGetDeskundigen({ pageSize: constants.defaultMaxPageSize });

  const formId = 'WebGebruikerEditForm';

  return (
    <>
      {isMilieudienstenLoading || isBevoegdGezagLoading || isDeelnemerLoading || isDeskundigenLoading || isWebGebruikerLoading ? (
        <Spin size="large" />
      ) : (
        <Card
          title="Web gebruiker aanpassen"
          extra={
            <Button type="primary" htmlType="submit" form={formId} disabled={isReadOnly}>
              Opslaan
            </Button>
          }>
          <WebGebruikerForm
            id={formId}
            onFinish={values => {
              updateWebgebruiker.mutate(values, {
                onSuccess: () => {
                  navigate('/webgebruiker');
                },
              });
            }}
            initialValues={{
              contactpersoon: webGebruikerResponse?.contactpersoon,
              functieContactpersoon: webGebruikerResponse?.functieContactpersoon,
              ondertekenaar: webGebruikerResponse?.ondertekenaar,
              functieOndertekenaar: webGebruikerResponse?.functieOndertekenaar,
              webGebruikerGroepId: webGebruikerResponse?.webGebruikerGroepId,
              bevoegdgezagId: webGebruikerResponse?.bevoegdgezagId,
              deelnemerId: webGebruikerResponse?.deelnemerId,
              milieudienstId: webGebruikerResponse?.milieudienstId,
              deskundigeId: webGebruikerResponse?.deskundigeId,
              loginNaam: webGebruikerResponse?.loginNaam,
              email: webGebruikerResponse?.email,
              keyUser: webGebruikerResponse?.keyUser,
              kvkNummer: webGebruikerResponse?.kvkNummer,
            }}
            initialWebGebruikerGroep={webGebruikerResponse?.webGebruikerGroepId}
            milieudiensten={milieudienstenResponse ?? []}
            deskundigen={deskundigenResponse ?? []}
            bevoegdGezagen={bevoegdGezagResponse ?? []}
            deelnemers={deelnemerResponse?.data ?? []}
            isCreate={false}
            readOnly={isReadOnly}
          />
          <ChangeLog
            items={webGebruikerResponse?.toelichtingen ?? []}
            loading={isWebGebruikerLoading}
            title={t('webGebruiker.toelichtingWijzigingenTitel')}
            emptyText={t('webGebruiker.toelichtingenLeeg')}
          />
        </Card>
      )}
    </>
  );
};
